/**
 * useLyricOrchestrator — React hook for multi-agent lyric generation.
 * Wraps runLyricDeliberation with state management, abort control, and mounted guard.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { runLyricDeliberation } from '@/services/lyricOrchestratorService';
import type {
  LyricDeliberationResult,
  LyricDeliberationStatus,
  LyricProgressEvent,
  LyricDeliberationConfig,
  LyricArchitectureContext,
} from '@/types/lyricOrchestrator.types';

export function useLyricOrchestrator() {
  const [status, setStatus] = useState<LyricDeliberationStatus>('idle');
  const [result, setResult] = useState<LyricDeliberationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<LyricProgressEvent | null>(null);
  const [completedAgents, setCompletedAgents] = useState<string[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const startLyricDeliberation = useCallback(async (
    seed: string,
    genre: string,
    moods: string[],
    arch?: LyricArchitectureContext,
    config?: Partial<LyricDeliberationConfig>,
  ) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setStatus('drafting');
    setResult(null);
    setError(null);
    setProgress(null);
    setCompletedAgents([]);

    try {
      const deliberationResult = await runLyricDeliberation(
        seed,
        genre,
        moods,
        arch,
        config,
        abortRef.current.signal,
        (event) => {
          if (!mountedRef.current) return;
          setProgress(event);
          if (event.type === 'agent-complete') {
            setCompletedAgents(prev => [...prev, event.agentId]);
          }
          if (event.type === 'synthesis-start') {
            setStatus('synthesizing');
          }
        }
      );
      if (!mountedRef.current) return;
      setResult(deliberationResult);
      setStatus('complete');
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      if (err instanceof DOMException && err.name === 'AbortError') {
        setStatus('aborted');
        return;
      }
      setError(err instanceof Error ? err.message : 'Lyric deliberation failed');
      setStatus('failed');
    }
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    if (mountedRef.current) {
      setStatus('aborted');
    }
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setResult(null);
    setError(null);
    setProgress(null);
    setCompletedAgents([]);
  }, []);

  return {
    status,
    isWriting: status === 'drafting' || status === 'synthesizing',
    result,
    error,
    progress,
    completedAgents,
    startLyricDeliberation,
    cancel,
    reset,
  };
}
